<template>
  <div class="tw-mb-6 tw-grid tw-grid-cols-9 tw-gap-6 max-xl:tw-grid-cols-10">
    <div class="tw-col-span-3 max-xl:tw-col-span-5 max-md:tw-col-span-10">
      <cl-data-card
        data-test-id="msp-emails-analyzed-card"
        :icon-tooltip="{ content: tooltipEmailSecurity, placement: 'top' }"
        :card-tooltip="{ content: tooltipEmailsAnalyzed, placement: 'top' }"
      >
        <template #icon>
          <component :is="SERVICE_ICON.PHISH_SERVICE" :width="16" />
        </template>
        <template #title>
          {{ $t("emails-analyzed-legend") }}
        </template>
        <template #footer>
          <cl-data-card-footer-loading v-if="loading" />
          <cl-data-card-footer-big-number v-else>
            <span
              :data-test-id="`${MSP}-email-security-email-analyzed-count`"
              >{{ formatNumberCompat(totalEmailsAnalyzed) }}</span
            >
          </cl-data-card-footer-big-number>
        </template>
      </cl-data-card>
    </div>
    <div
      class="tw-col-span-3 max-xl:tw-col-span-5 max-md:tw-col-span-10"
      :data-test-id="'msp-overview-card-incidents-route'"
    >
      <router-link v-slot="{ href, navigate }" :to="incidentsHref" custom>
        <cl-data-card
          data-test-id="msp-incidents-card"
          :link="{ href, navigate, label: $t('incidents-legend') }"
          :icon-tooltip="{ content: tooltipEmailSecurity, placement: 'top' }"
          :card-tooltip="{ content: tooltipIncidents, placement: 'top' }"
        >
          <template #icon>
            <component :is="SERVICE_ICON.PHISH_SERVICE" :width="16" />
          </template>
          <template #footer>
            <cl-data-card-footer-loading v-if="loading" />
            <cl-data-card-footer-big-number v-else>
              <span :data-test-id="`${MSP}-email-security-incidents-count`">
                {{ formatNumberCompat(totalIncidents) }}
              </span>
            </cl-data-card-footer-big-number>
          </template>
        </cl-data-card>
      </router-link>
    </div>
    <div
      class="tw-col-span-3 max-xl:tw-col-span-5 max-md:tw-col-span-10"
      :data-test-id="'msp-overview-card-active-users-licences-route'"
    >
      <router-link
        v-slot="{ href, navigate }"
        :to="{ name: `${MSP}UsageRoute` }"
        custom
      >
        <cl-data-card
          data-test-id="msp-active-users-card"
          :link="{
            href,
            navigate,
            label: $t('active-users-licenses-issued'),
          }"
          :icon-tooltip="{ content: tooltipEmailSecurity, placement: 'top' }"
          :card-tooltip="{ content: tooltipActiveUsers, placement: 'top' }"
        >
          <template #icon>
            <component :is="SERVICE_ICON.PHISH_SERVICE" :width="16" />
          </template>
          <template #footer>
            <cl-data-card-footer-loading v-if="loading" />
            <cl-data-card-footer-big-number v-else>
              <span
                :data-test-id="`${MSP}-email-security-active-users-count`"
                >{{ totalActiveUsers }}</span
              >
              <cl-data-card-footer-small-number>
                /<span
                  :data-test-id="`${MSP}-email-security-licences-issued-count`"
                  >{{ licencesIssued }}</span
                >
              </cl-data-card-footer-small-number>
            </cl-data-card-footer-big-number>
          </template>
        </cl-data-card>
      </router-link>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from "vue";
import { useAuthenticationStore } from "@/store/authentication.store";
import { storeToRefs } from "pinia";
import { useHistoryStore } from "@/store/history.store";

import { useGlobalOrganisationIDBasedOnRoute } from "@/composables/useGlobalOrganisationIDBasedOnRoute";
import dayjs from "dayjs";
import { MSP } from "@/constants";
import buildOrgUrl from "@/utils/navigation/orgUrl";
import { PHISH_SERVICE } from "@/utils/services.ts";
import { useI18n } from "vue-i18n";
import { SERVICE_ICON } from "@/utils/services.ts";
import { formatNumberCompat } from "@/utils/formatNumberCompat";
import {
  LicenceWithSeats,
  ResellerOrganisationResponse,
} from "@/types/licenceSolutions.ts";
import { useLicenceStore } from "@/modules/Usage/store/licence.store";

const props = defineProps({
  startDate: {
    type: String,
    required: true,
  },
});

const { globalOrganisationId } = useGlobalOrganisationIDBasedOnRoute();

const loading = ref(true);

const authStore = useAuthenticationStore();
const historyStore = useHistoryStore();
const licenceStore = useLicenceStore();
const { totalEmailsAnalyzed, totalIncidents } = storeToRefs(historyStore);
const { collection } = storeToRefs(licenceStore);

const incidentsHref = computed(() => {
  const fallbackUuid = authStore.selectedMsp?.uuid;
  return buildOrgUrl(
    "msp",
    globalOrganisationId.value || fallbackUuid,
    "email_security/incidents",
  );
});

const allLicences = computed<ResellerOrganisationResponse | null>(
  () => collection.value as ResellerOrganisationResponse | null,
);
const licences = computed<LicenceWithSeats[]>(
  () => allLicences.value?.licences ?? [],
);

const licencesIssued = computed(() => {
  if (licences.value.length === 0) {
    return "0";
  }

  const licence: LicenceWithSeats | undefined = licences.value.find(
    (licence: LicenceWithSeats) => licence.solution_uuid === PHISH_SERVICE,
  );

  return formatNumberCompat(licence?.seats_issued ?? 0);
});
const totalActiveUsers = computed(() => {
  if (licences.value.length === 0) {
    return "0";
  }

  const licence: LicenceWithSeats | undefined = licences.value.find(
    (licence: LicenceWithSeats) => licence.solution_uuid === PHISH_SERVICE,
  );

  return formatNumberCompat(licence?.seats_used ?? 0);
});

const { t } = useI18n();
const tooltipEmailSecurity = t("email-security-solution");
const tooltipEmailsAnalyzed = t("emails-analyzed-tooltip");
const tooltipIncidents = t("incidents-tooltip");
const tooltipActiveUsers = t("active-users-licenses-issued-email-security");

historyStore.setTotalEmailsAnalyzed(0);
historyStore.setTotalIncidents(0);

const fetchIncidentsAndEmails = async () => {
  try {
    loading.value = true;

    const params = {
      start_date: props.startDate,
      end_date: dayjs().format("YYYY-MM-DD"),
      organisationType: MSP,
    };

    await Promise.allSettled([
      historyStore.getTotalEmailsAnalyzed({
        organisationId: globalOrganisationId.value,
        params,
      }),
      historyStore.getTotalIncidents({
        organisationId: globalOrganisationId.value,
        params,
      }),
      licenceStore.listAllLicences({
        uuid: globalOrganisationId.value,
      }),
    ]);
  } finally {
    loading.value = false;
  }
};

watch(
  () => props.startDate,
  () => {
    fetchIncidentsAndEmails();
  },
  { immediate: true },
);
</script>
